/* ADEM DESIGN LAB v1.3 - Reveal, Scroll-Fortschritt, Lazy-Video, Lite-Player.
   Kein Scroll-Hijacking, keine Bibliothek. Opacity/Transform/Clip-Path only. */
(() => {
  // Mehrere Sections binden die Datei ein - nur einmal initialisieren
  if (window.__ademLab) return;
  window.__ademLab = true;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Reveal & Wipe beim Eintritt in den Viewport
  const revealIO = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            // Masken-Elemente werden über ihr Elternelement beobachtet (voll maskiert = für IO unsichtbar)
            (e.target.__ademWipe || e.target).classList.add('is-in');
            revealIO.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 })
    : null;

  // Videos erst in Sichtnähe laden, außerhalb pausieren (Datenvolumen, Akku)
  const videoIO = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const v = e.target;
          if (e.isIntersecting) {
            if (!v.dataset.loaded) {
              const small = window.matchMedia('(max-width: 749px)').matches;
              const hlsOk = v.canPlayType('application/vnd.apple.mpegurl') !== '';
              v.querySelectorAll('source[data-src-d]').forEach((s) => {
                if (small && hlsOk && s.dataset.srcHls) { s.src = s.dataset.srcHls; s.type = 'application/vnd.apple.mpegurl'; }
                else s.src = small ? s.dataset.srcM : s.dataset.srcD;
              });
              v.load();
              v.dataset.loaded = '1';
            }
            if (!reduce) v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { rootMargin: '25% 0px' })
    : null;

  // Scroll-Fortschritt je Element (0 = unten rein, 1 = oben raus) als CSS-Variable --adem-p
  let tracked = [];
  let ticking = false;
  const update = () => {
    const vh = window.innerHeight;
    tracked.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
      el.style.setProperty('--adem-p', p.toFixed(3));
    });
    updateSignals();
    updatePortals();
    ticking = false;
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };

  // YouTube erst nach Klick laden (Performance + keine Drittanbieter-Cookies vor Interaktion)
  const initPlayers = (root) => {
    root.querySelectorAll('[data-adem-yt]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        const id = btn.dataset.ademYt;
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;
        iframe.title = btn.getAttribute('aria-label') || 'Video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        btn.parentElement.appendChild(iframe);
        btn.remove();
      });
    });
  };

  // ---------- Signal -> Schmetterling -------------------------------------------------
  // Liest gerade Segmente aus dem eingebetteten SVG (line, polyline, polygon, path mit M/L/H/V/Z)
  const segmentsFrom = (svg) => {
    const out = [];
    const push = (a, b) => { if (Math.hypot(b[0] - a[0], b[1] - a[1]) > 0.5) out.push([a[0], a[1], b[0], b[1]]); };
    svg.querySelectorAll('line').forEach((l) => push([+l.getAttribute('x1'), +l.getAttribute('y1')], [+l.getAttribute('x2'), +l.getAttribute('y2')]));
    svg.querySelectorAll('polyline, polygon').forEach((p) => {
      const n = (p.getAttribute('points') || '').trim().split(/[\s,]+/).map(Number);
      const pts = []; for (let i = 0; i + 1 < n.length; i += 2) pts.push([n[i], n[i + 1]]);
      for (let i = 1; i < pts.length; i++) push(pts[i - 1], pts[i]);
      if (p.tagName.toLowerCase() === 'polygon' && pts.length > 2) push(pts[pts.length - 1], pts[0]);
    });
    svg.querySelectorAll('path').forEach((p) => {
      const tokens = (p.getAttribute('d') || '').match(/[a-zA-Z]|-?\d*\.?\d+(?:e-?\d+)?/g) || [];
      let cmd = '', cur = [0, 0], start = [0, 0], i = 0;
      const num = () => +tokens[i++];
      while (i < tokens.length) {
        if (/[a-zA-Z]/.test(tokens[i])) cmd = tokens[i++];
        const rel = cmd === cmd.toLowerCase(), c = cmd.toUpperCase();
        if (c === 'M') { const x = num(), y = num(); cur = rel ? [cur[0] + x, cur[1] + y] : [x, y]; start = cur; cmd = rel ? 'l' : 'L'; }
        else if (c === 'L') { const x = num(), y = num(); const nx = rel ? [cur[0] + x, cur[1] + y] : [x, y]; push(cur, nx); cur = nx; }
        else if (c === 'H') { const x = num(); const nx = [rel ? cur[0] + x : x, cur[1]]; push(cur, nx); cur = nx; }
        else if (c === 'V') { const y = num(); const nx = [cur[0], rel ? cur[1] + y : y]; push(cur, nx); cur = nx; }
        else if (c === 'Z') { push(cur, start); cur = start; }
        else { i++; } // Kurven werden ignoriert (Butterfly besteht aus Geraden)
      }
    });
    return out;
  };
  const clamp01 = (v) => Math.min(1, Math.max(0, v));
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const signals = [];
  const initSignals = (root) => {
    root.querySelectorAll('[data-adem-signal]').forEach((sec) => {
      if (sec.__adem || sec.__ademLoading) return;
      const live = sec.querySelector('.adem-signal__live');
      const holder = sec.querySelector('.adem-signal__source');
      if (!live || !holder) return;
      const inline = holder.querySelector('svg');
      if (inline) { setupSignal(sec, live, inline); return; }
      const url = holder.getAttribute('data-adem-signal-src');
      if (!url || !window.fetch || !window.DOMParser) return;
      sec.__ademLoading = true;
      fetch(url).then((r) => (r.ok ? r.text() : '')).then((txt) => {
        const svg = txt && new DOMParser().parseFromString(txt, 'image/svg+xml').querySelector('svg');
        if (svg) { setupSignal(sec, live, svg); updateSignals(); }
      }).catch(() => {}).finally(() => { sec.__ademLoading = false; });
    });
  };
  const setupSignal = (sec, live, src) => {
    const vb = src.viewBox && src.viewBox.baseVal && src.viewBox.baseVal.width ? src.viewBox.baseVal : { width: 1000, height: 723 };
    live.setAttribute('viewBox', `0 0 ${vb.width} ${vb.height}`);
    // Original-Vektor (gefüllte Pfade, Kurven): Geometrie bleibt unangetastet, animiert wird nur per Transformation
    if (!src.querySelector('line, polyline, polygon')) {
      const NS = 'http://www.w3.org/2000/svg', id = 'adem-sig-mask-' + signals.length;
      const cx = vb.width / 2, midY = vb.height / 2, maxR = Math.hypot(vb.width, vb.height) / 2;
      const defs = document.createElementNS(NS, 'defs'), mask = document.createElementNS(NS, 'mask');
      mask.setAttribute('id', id); mask.setAttribute('maskUnits', 'userSpaceOnUse');
      mask.setAttribute('x', '0'); mask.setAttribute('y', '0'); mask.setAttribute('width', vb.width); mask.setAttribute('height', vb.height);
      const circle = document.createElementNS(NS, 'circle');
      circle.setAttribute('cx', cx); circle.setAttribute('cy', midY); circle.setAttribute('r', '0'); circle.setAttribute('fill', '#fff');
      mask.appendChild(circle); defs.appendChild(mask); live.appendChild(defs);
      const outer = document.createElementNS(NS, 'g'); outer.setAttribute('mask', `url(#${id})`);
      const g = document.createElementNS(NS, 'g'); g.setAttribute('class', 'adem-signal__exact');
      src.querySelectorAll('path').forEach((pp) => g.appendChild(document.importNode(pp, true)));
      outer.appendChild(g); live.appendChild(outer);
      live.querySelector('.adem-signal__path').setAttribute('d', '');
      sec.__adem = { exact: { g, circle, maxR }, vb, midY, cx, segs: [], maxD: 1,
        path: live.querySelector('.adem-signal__path'), wave: live.querySelector('.adem-signal__wave'),
        fill: sec.querySelector('[data-adem-signal-fill]'), caption: sec.querySelector('.adem-signal__caption') };
      sec.classList.add('is-ready');
      signals.push(sec);
      renderSignal(sec, reduce ? 1 : 0);
      return;
    }
    const segs = segmentsFrom(src);
    if (!segs.length) return;
    const midY = vb.height * 0.5, cx = vb.width / 2;
    let maxD = 0;
    segs.forEach((s) => { s.d = Math.hypot((s[0] + s[2]) / 2 - cx, (s[1] + s[3]) / 2 - midY); maxD = Math.max(maxD, s.d); });
    sec.__adem = { segs, vb, midY, cx, maxD,
      path: live.querySelector('.adem-signal__path'), wave: live.querySelector('.adem-signal__wave'),
      fill: sec.querySelector('[data-adem-signal-fill]'), caption: sec.querySelector('.adem-signal__caption') };
    sec.classList.add('is-ready');
    signals.push(sec);
    renderSignal(sec, reduce ? 1 : 0);
  };
  const renderSignal = (sec, p) => {
    const S = sec.__adem; if (!S) return;
    const { segs, vb, midY, cx, maxD } = S;
    // 1) Signal-Zeile füllt sich
    if (S.fill) S.fill.style.setProperty('--adem-fill', (clamp01(p / 0.26) * 100).toFixed(1));
    // 2) Frequenzlinie wächst, schwingt, verklingt während der Formung
    const grow = easeOut(clamp01((p - 0.2) / 0.14));
    const form = clamp01((p - 0.4) / 0.4);
    const amp = 26 * clamp01((p - 0.3) / 0.1) * (1 - form);
    const half = (vb.width * 0.5) * grow;
    let w = '';
    const steps = 90;
    for (let i = 0; i <= steps; i++) {
      const x = cx - half + (2 * half * i) / steps;
      const env = Math.sin(Math.PI * i / steps);                 // Hüllkurve: Mitte stärker
      const y = midY + amp * env * Math.sin(i * 0.55 + p * 40);   // Phase folgt dem Scroll
      w += (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1);
    }
    S.wave.setAttribute('d', grow > 0 ? w : '');
    S.wave.style.opacity = (1 - form * 0.9).toFixed(2);
    // 3a) Original-Vektor: faltet sich aus der Linie auf (nur Skalierung), Aufdeckung von innen nach außen
    if (S.exact) {
      const e = easeOut(form), sx = 0.35 + 0.65 * e, sy = 0.012 + 0.988 * e;
      S.exact.g.setAttribute('transform', form >= 1 ? '' : `translate(${(cx * (1 - sx)).toFixed(2)} ${(midY * (1 - sy)).toFixed(2)}) scale(${sx.toFixed(4)} ${sy.toFixed(4)})`);
      S.exact.circle.setAttribute('r', (S.exact.maxR * clamp01(form * 1.45)).toFixed(1));
      S.exact.g.style.opacity = form > 0 ? (0.35 + 0.65 * form).toFixed(2) : '0';
      if (S.caption) S.caption.classList.toggle('is-in', form > 0.92);
      return;
    }
    // 3) Kanten heben sich aus der Linie in die Schmetterlingsform (innen zuerst)
    let d = '';
    if (form > 0) {
      for (const s of segs) {
        const t = easeOut(clamp01(form * 1.45 - (s.d / maxD) * 0.45));
        if (t <= 0) continue;
        const y1 = midY + (s[1] - midY) * t, y2 = midY + (s[3] - midY) * t;
        const x1 = cx + (s[0] - cx) * (0.35 + 0.65 * t), x2 = cx + (s[2] - cx) * (0.35 + 0.65 * t);
        d += `M${x1.toFixed(1)} ${y1.toFixed(1)}L${x2.toFixed(1)} ${y2.toFixed(1)}`;
      }
    }
    S.path.setAttribute('d', d);
    S.path.style.opacity = (0.35 + 0.65 * form).toFixed(2);
    if (S.caption) S.caption.classList.toggle('is-in', form > 0.92);
  };
  const updateSignals = () => {
    const vh = window.innerHeight;
    signals.forEach((sec) => {
      const r = sec.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      renderSignal(sec, reduce ? 1 : clamp01(-r.top / Math.max(1, r.height - vh)));
    });
  };

  // ---------- Portal (Section 06): Foto -> Stickerei -> Vektor -> "Klang." -> Finale ------------
  const portals = [];
  const lerp = (a, b, t) => a + (b - a) * t;
  const sstep = (t) => { t = clamp01(t); return t * t * (3 - 2 * t); };
  const span = (p, a, b) => clamp01((p - a) / (b - a));
  const expo = (a, b, t) => a * Math.pow(b / a, sstep(t));
  const WING_W = 1000, WING_H = 1000 * 1184.04 / 1665.47;   // Basisgröße des Vektors (px), skaliert per transform
  const initPortals = (root) => {
    if (reduce) return;                                       // Reduced Motion: statisches Finale
    root.querySelectorAll('[data-adem-portal]').forEach((sec) => {
      if (sec.__ademPortal) return;
      const q = (c) => sec.querySelector(c);
      const cam = q('.adem-portal__cam');
      if (!cam) return;
      sec.__ademPortal = {
        ex: +sec.dataset.ex / 100, ey: +sec.dataset.ey / 100, ew: +sec.dataset.ew / 100,
        asp: +cam.dataset.aspect || 1.5, rx: +cam.dataset.rx / 100, ry: +cam.dataset.ry / 100, rw: +cam.dataset.rw / 100,
        cam, detail: q('.adem-portal__detail'), shade: q('.adem-portal__shade'), wing: q('.adem-portal__wing'),
        world: q('.adem-portal__world'), knock: q('.adem-portal__knock'), edge: q('.adem-portal__edge'),
        kspan: q('.adem-portal__knock span'), espan: q('.adem-portal__edge span'), ring: q('.adem-portal__ring'),
        finale: q('.adem-portal__finale'), lead: q('.adem-portal__lead'), label: q('.adem-portal__label'),
        d1: q('.adem-portal__dim-1'), d2: q('.adem-portal__dim-2'), side: q('.adem-portal__side'), W: 0, H: 0
      };
      sec.classList.add('is-live');
      portals.push(sec);
      // Foto und Ausschnitt früh laden (2 Bildschirme vorher), sonst startet die Fahrt schwarz
      if ('IntersectionObserver' in window) {
        const pre = new IntersectionObserver((es) => es.forEach((en) => {
          if (!en.isIntersecting) return;
          sec.querySelectorAll('.adem-portal__cam img, .adem-portal__wing').forEach((im) => { im.loading = 'eager'; });
          pre.disconnect();
        }), { rootMargin: '200% 0px' });
        pre.observe(sec);
      }
      renderPortal(sec, 0);
    });
  };
  const renderPortal = (sec, p) => {
    const P = sec.__ademPortal; if (!P) return;
    const W = sec.clientWidth || window.innerWidth, H = window.innerHeight, va = H / W, mob = W < 750;
    if (W !== P.W || H !== P.H) {                             // Größen nur bei Resize setzen
      P.W = W; P.H = H;
      P.cam.style.width = W + 'px';
      if (P.detail) Object.assign(P.detail.style, { left: P.rx * W + 'px', top: P.ry * P.asp * W + 'px', width: P.rw * W + 'px', height: P.rw * W + 'px' });
      P.wing.style.width = WING_W + 'px';
    }
    const ey = P.ey * P.asp;                                  // alles in Fotobreiten
    const fit = (vw, cx, cy) => {                             // Kamera im Foto halten
      const vh = vw * va;
      return [Math.min(Math.max(cx, vw / 2), 1 - vw / 2), Math.min(Math.max(cy, vh / 2), P.asp - vh / 2)];
    };
    const vw0 = mob ? Math.min(0.95, (P.asp / va) * 0.98) : 0.8;
    const [cx0, cy0] = fit(vw0, mob ? 0.5 : 0.55 - 0.15 * vw0, ey + 0.1 * vw0 * va);
    const vw1 = P.ew / (mob ? 0.30 : 0.18);
    const tx = mob ? 0.5 : 0.64, ty = mob ? 0.6 : 0.58;
    const cx1 = P.ex - (tx - 0.5) * vw1, cy1 = ey - (ty - 0.5) * vw1 * va;
    const t = span(p, 0.10, 0.38), u = sstep(t);
    const vw = expo(vw0, vw1, t), cx = lerp(cx0, cx1, u), cy = lerp(cy0, cy1, u), sc = W / vw;
    P.cam.style.transform = `translate3d(${W / 2 - cx * sc}px, ${H / 2 - cy * sc}px, 0) scale(${1 / vw})`;
    P.cam.style.filter = `saturate(${(0.35 + 0.65 * sstep(span(p, 0.27, 0.36))).toFixed(3)})`;
    if (P.detail) P.detail.style.opacity = clamp01((0.55 - vw) / 0.2).toFixed(3);
    P.shade.style.opacity = (0.95 * sstep(span(p, 0.36, 0.43)) + 0.05 * span(p, 0.44, 0.47)).toFixed(3);
    // Vektor: liegt deckungsgleich auf der Stickerei, dann Flug in den Konvergenzpunkt der Flügel
    const exs = W / 2 + (P.ex - cx) * sc, eys = H / 2 + (ey - cy) * sc, ews = P.ew * sc;
    const d = span(p, 0.39, 0.51), k = expo(1, 11, d), e = sstep(d);
    const ax = lerp(0.5, 0.4976, e), ay = lerp(0.5, 0.4776, e), ws = (ews * k) / WING_W;
    P.wing.style.transform = `translate3d(${exs - ax * WING_W * ws}px, ${eys - ay * WING_H * ws}px, 0) scale(${ws})`;
    P.wing.style.opacity = (sstep(span(p, 0.29, 0.34)) * (1 - sstep(span(p, 0.47, 0.50)))).toFixed(3);
    P.wing.classList.toggle('is-glow', k < 2.5);
    // "Klang.": neue Welt nur in den Buchstaben, Impulsring, Buchstaben öffnen sich
    const kv = sstep(span(p, 0.48, 0.505)), g = expo(1, 40, span(p, 0.59, 0.74));
    P.world.style.opacity = kv.toFixed(3);
    P.knock.style.opacity = (kv * (1 - sstep(span(p, 0.71, 0.74)))).toFixed(3);
    P.edge.style.opacity = (kv * (1 - sstep(span(p, 0.60, 0.66)))).toFixed(3);
    P.kspan.style.transform = P.espan.style.transform = `scale(${g.toFixed(4)})`;
    const r = span(p, 0.49, 0.57);
    P.ring.style.opacity = r > 0 && r < 1 ? (1 - r).toFixed(3) : '0';
    P.ring.style.transform = `scale(${(r * 1.2).toFixed(3)})`;
    // Finale
    const fin = sstep(span(p, 0.79, 0.86)); P.finale.style.opacity = fin.toFixed(3);
    const lead = sstep(span(p, 0, 0.06)) * (1 - sstep(span(p, 0.45, 0.49)));
    P.lead.style.opacity = lead.toFixed(3); if (P.label) P.label.style.opacity = lead.toFixed(3);
    const d1 = sstep(span(p, 0.80, 0.86)), d2 = sstep(span(p, 0.86, 0.92)), sd = sstep(span(p, 0.90, 0.96));
    P.d1.style.opacity = d1.toFixed(3); P.d1.style.transform = `translate3d(0, ${(1 - d1) * 24}px, 0)`;
    P.d2.style.opacity = d2.toFixed(3); P.d2.style.transform = `translate3d(0, ${(1 - d2) * 24}px, 0)`;
    P.side.style.opacity = sd.toFixed(3);
    sec.classList.toggle('is-end', sd > 0.5);
    sec.style.setProperty('--adem-scrim', Math.max(lead * 0.8, fin).toFixed(3));
  };
  const updatePortals = () => {
    const vh = window.innerHeight;
    portals.forEach((sec) => {
      const r = sec.getBoundingClientRect();
      if (r.bottom < -vh || r.top > vh * 1.5) return;
      renderPortal(sec, clamp01(-r.top / Math.max(1, r.height - vh)));
    });
  };

  const init = (root = document) => {
    initSignals(root);
    initPortals(root);
    root.querySelectorAll('.adem-lab [data-adem-reveal], .adem-lab [data-adem-wipe]').forEach((el) => {
      if (reduce || !revealIO) { el.classList.add('is-in'); return; }
      if (el.hasAttribute('data-adem-wipe') && el.parentElement) {
        el.parentElement.__ademWipe = el;
        revealIO.observe(el.parentElement);
      } else {
        revealIO.observe(el);
      }
    });
    root.querySelectorAll('.adem-lab video[data-adem-lazy]').forEach((v) => { if (videoIO) videoIO.observe(v); });
    if (!reduce) tracked = Array.from(document.querySelectorAll('.adem-lab [data-adem-scroll]'));
    initPlayers(root);
    update();
  };

  document.addEventListener('DOMContentLoaded', () => init());
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  // Theme-Editor: Sections werden neu geladen
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

